// Her COURSES — the Level 1 of the Courses tool.
//
// Her call, 2026-09-02: "I do think people have more than one course." A course
// is the thing an offer delivers; its modules belong to it rather than to the
// account.
//
// Backed by `courses` from supabase/0015_sites_and_courses.sql, whose backfill
// gave every account that had modules one course, named from its Signature
// Offer where there was one and "My course" where there wasn't. So there is
// always at least one by the time this loads.
//
// THE OFFER LIVES HERE AND NOT ON A MODULE (0015's note 4). "Which offer does
// this teach" gets one answer; a module inherits it from its course. Putting
// `offer_id` on both would give that question two answers that can disagree,
// which is the exact failure CLAUDE.md's facts check exists for.
//
// `seeded` IS NOT READ YET. The once-ever pull from a locked curriculum is
// still gated on the per-account `settings.course_seeded` flag that App owns,
// exactly as it was. The column is here because 0015 created it and the spec
// (8.5) wants the flag to become per course — but moving it is a change to what
// decides whether her curriculum gets pulled, which is not a thing to fold into
// a change about switchers. Until then there is one answer to "has the seed
// happened", and it is still the account flag.

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useUser } from "@clerk/clerk-react";
import { createRecordStore, derivedId } from "./recordStore";
import { reorderWrites } from "./reorder";

// `id`, `createdAt`, `updatedAt` are handled by recordStore — not listed.
export const COURSE_FIELDS = ["title", "offerId", "seeded", "sortOrder", "status"];

/** What a course is called in a list. Never blank. */
export const courseLabel = (course) =>
  String(course?.title || "").trim() || "Untitled course";

const useCourseRecords = createRecordStore({
  table: "courses",
  fields: COURSE_FIELDS,
  idPrefix: "crs",
  orderBy: "sort_order",
  ascending: true,
});

export function useCourses() {
  const { rows, loading, error, save, remove, reload, clearError, newId } = useCourseRecords();

  const courses = useMemo(
    () => [...rows].sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0)),
    [rows],
  );

  const addCourse = useCallback(async () => save({
    id: newId(),
    title: "New course",
    /* `seeded` false on a course she made herself: nothing has been pulled into
       it. Unread for now (see the note at the top), written so that the day it
       IS read, a course added today already says the true thing. */
    seeded: false,
    status: "draft",
    sortOrder: courses.reduce((max, c) => Math.max(max, (c?.sortOrder ?? 0) + 1), 0),
  }), [courses, save, newId]);

  /* HER FIRST COURSE, made on its own — the mirror of the first site, for the
     same reason and with the same guards. Without it a new account has no
     course, so no module can be added and the once-ever curriculum pull has
     nowhere to land.

     `seeded` FALSE, deliberately, even though the backfill wrote true for the
     courses it made. The backfill's courses already had modules in them, so the
     seed had plainly happened; this one is empty and nothing has been pulled
     into it yet. The column is still unread (see the note at the top) — this is
     it recording the truth for the day it is. */
  const { user } = useUser();
  const making = useRef(false);
  const madeOnce = useRef(false);
  useEffect(() => {
    if (loading || error || making.current || madeOnce.current || courses.length || !user?.id) return;
    making.current = true;
    madeOnce.current = true;
    save({
      id: derivedId("crs", user.id),
      title: "My course",
      seeded: false,
      status: "draft",
      sortOrder: 0,
    }).finally(() => { making.current = false; });
  }, [loading, error, courses.length, user?.id, save]);

  const reorderCourses = useCallback(async (from, to) => {
    let ok = true;
    for (const c of reorderWrites(courses, from, to)) if (!(await save(c))) ok = false;
    return ok;
  }, [courses, save]);

  return {
    courses, loading, error, clearError, reload,
    saveCourse: save,
    /* DELETING A COURSE DELETES ITS MODULES — `lesson_plans.course_id` is
       `on delete cascade`. Same rule as a site and its pages: the caller says
       so first, with the count, in a ConfirmDialog. */
    deleteCourse: remove,
    addCourse,
    reorderCourses,
    newCourseId: newId,
  };
}
